import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface CategoryInter {
  id: number;
  slug: string;
  name: string;
}

const Category: React.FC = () => {
  const api = process.env.REACT_APP_API_URL as string;
  const [tags, setTags] = useState<CategoryInter[]>([]);
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  // Fetch all tags on mount
  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const res = await axios.get<CategoryInter[]>(`${api}/auth/category`);
      setTags(res.data);
    } catch (err) {
      console.error('Error fetching category:', err);
    }
  };

  const handleAddOrEditTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!slug.trim() || !name.trim()) return;

    try {
      if (editingId !== null) {
        // Edit existing tag
        await axios.put(`${api}/auth/category/${editingId}`, { slug, name });
        
      } else {
        // Add new tag
        await axios.post(`${api}/auth/category`, { slug, name });
      }

      setSlug('');
      setName('');
      setEditingId(null);
      fetchTags();
    } catch (err) {
      console.error('Error saving category:', err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${api}/auth/category/${id}`);
      fetchTags();
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const handleEdit = (cateIn: CategoryInter) => {
    setSlug(cateIn.slug);
    setName(cateIn.name);
    setEditingId(cateIn.id);
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 p-6">
      {/* Sticky Form Section */}
      <div className="w-full md:w-1/2 relative">
        <div className="sticky top-6 bg-white shadow-lg rounded-xl p-6 h-max">
          <h2 className="text-xl font-semibold mb-4">
            {editingId !== null ? 'Edit Category' : 'Add New Category'}
          </h2>
          <form onSubmit={handleAddOrEditTag} className="space-y-4">
            <div>
              <label className="block font-medium mb-1">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                placeholder="Enter slug"
              />
            </div>
            <div>
              <label className="block font-medium mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring focus:border-blue-400"
                placeholder="Enter name"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                {editingId !== null ? 'Update Tag' : 'Add Tag'}
              </button>
              {editingId !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setSlug('');
                    setName('');
                    setEditingId(null);
                  }}
                  className="text-sm text-red-500 hover:underline"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Tag List Section */}
      <div className="w-full md:w-1/2 bg-gray-50 shadow-inner rounded-xl p-6 max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">All Category</h2>
        {tags.length === 0 ? (
          <p className="text-gray-500">No category added yet.</p>
        ) : (
          <ul className="space-y-2">
            {tags.map((tag) => (
              <li
                key={tag.id}
                className="border border-gray-300 rounded-md p-3 flex justify-between items-center"
              >
                <div>
                  <span className="font-bold text-blue-600">{tag.name}</span>{' '}
                  <span className="text-sm text-gray-500">({tag.slug})</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(tag)}
                    className="text-sm text-yellow-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(tag.id)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Category;
